import { useEffect } from 'react';
import { Link } from 'react-router-dom';
import Header from '@/components/feature/Header';
import Footer from '@/components/feature/Footer';
import SocialSidebar from '@/components/feature/SocialSidebar';
import { useSeoMeta } from '@/hooks/useSeoMeta';
import { whatsappHref } from '@/lib/contactProtect';

export interface ServicePageProps {
  lang: 'en' | 'tr';
  slug: string;
  icon: string;
  title: string;
  subtitle: string;
  heroImage: string;
  description: string;
  longDescription: string[];
  features: { icon: string; title: string; text: string }[];
  faq: { q: string; a: string }[];
  relatedServices: { slug: string; title: string; icon: string }[];
  seo: {
    title: string;
    description: string;
    keywords: string;
  };
  onQuoteOpen?: () => void;
}

export default function ServicePageLayout({
  lang,
  slug,
  icon,
  title,
  subtitle,
  heroImage,
  description,
  longDescription,
  features,
  faq,
  relatedServices,
  seo,
}: ServicePageProps) {
  const isEN = lang === 'en';
  const basePath = isEN ? '/services' : '/tr/hizmetler';
  const homePath = isEN ? '/' : '/tr';

  useSeoMeta({
    lang,
    canonicalPath: `${basePath}/${slug}`,
    title: seo.title,
    description: seo.description,
    keywords: seo.keywords,
    ogLocale: isEN ? 'en_US' : 'tr_TR',
    ogImage: heroImage,
  });

  useEffect(() => {
    const siteUrl = 'https://www.canozen.com.tr';
    const pageUrl = `${siteUrl}${basePath}/${slug}`;

    const faqSchema = faq.length > 0 ? {
      '@context': 'https://schema.org',
      '@type': 'FAQPage',
      mainEntity: faq.map(({ q, a }) => ({
        '@type': 'Question',
        name: q,
        acceptedAnswer: { '@type': 'Answer', text: a },
      })),
    } : null;

    const serviceSchema = {
      '@context': 'https://schema.org',
      '@type': 'Service',
      name: title,
      description: seo.description,
      provider: {
        '@type': 'Organization',
        name: 'CSG The Event Company & IEM101 International',
        url: siteUrl,
      },
      url: pageUrl,
      image: heroImage,
      areaServed: [
        { '@type': 'Country', name: 'Turkey' },
        { '@type': 'Country', name: 'Hungary' },
        { '@type': 'Country', name: 'United Arab Emirates' },
        { '@type': 'Country', name: 'Germany' },
      ],
    };

    const breadcrumbSchema = {
      '@context': 'https://schema.org',
      '@type': 'BreadcrumbList',
      itemListElement: [
        { '@type': 'ListItem', position: 1, name: isEN ? 'Home' : 'Ana Sayfa', item: `${siteUrl}${homePath}` },
        { '@type': 'ListItem', position: 2, name: isEN ? 'Services' : 'Hizmetler', item: `${siteUrl}${basePath}` },
        { '@type': 'ListItem', position: 3, name: title, item: pageUrl },
      ],
    };

    const addSchema = (schema: object, id: string) => {
      const existing = document.getElementById(id);
      if (existing) existing.remove();
      const script = document.createElement('script');
      script.type = 'application/ld+json';
      script.id = id;
      script.textContent = JSON.stringify(schema);
      document.head.appendChild(script);
    };

    addSchema(serviceSchema, `svc-service-schema-${slug}`);
    addSchema(breadcrumbSchema, `svc-breadcrumb-schema-${slug}`);
    if (faqSchema) addSchema(faqSchema, `svc-faq-schema-${slug}`);

    return () => {
      [`svc-service-schema-${slug}`, `svc-breadcrumb-schema-${slug}`, `svc-faq-schema-${slug}`].forEach((id) => {
        const s = document.getElementById(id);
        if (s) s.remove();
      });
    };
  }, [slug, title, seo.description, heroImage, basePath, homePath, faq, isEN]);

  return (
    <div className="min-h-screen bg-black text-white" style={{ fontFamily: 'Inter, sans-serif' }}>
      <SocialSidebar lang={lang} />
      <Header lang={lang} />

      {/* Hero */}
      <section className="relative h-[60vh] min-h-[420px] flex items-end overflow-hidden">
        <img
          src={heroImage}
          alt={title}
          className="absolute inset-0 w-full h-full object-cover object-top"
        />
        <div className="absolute inset-0 bg-gradient-to-t from-black via-black/60 to-black/20" />
        <div className="relative z-10 w-full container mx-auto px-6 pb-12">
          {/* Breadcrumb */}
          <nav className="flex items-center gap-2 text-sm text-gray-400 mb-4">
            <Link to={homePath} className="hover:text-yellow-400 transition-colors">
              {isEN ? 'Home' : 'Ana Sayfa'}
            </Link>
            <i className="ri-arrow-right-s-line" />
            <Link to={basePath} className="hover:text-yellow-400 transition-colors">
              {isEN ? 'Services' : 'Hizmetler'}
            </Link>
            <i className="ri-arrow-right-s-line" />
            <span className="text-white">{title}</span>
          </nav>
          <div className="flex items-center gap-4 mb-3">
            <div className="w-14 h-14 bg-yellow-400 rounded-xl flex items-center justify-center flex-shrink-0">
              <i className={`${icon} text-2xl text-black`} />
            </div>
            <div>
              <p className="text-yellow-400 text-sm font-semibold uppercase tracking-widest">{subtitle}</p>
              <h1 className="text-4xl md:text-5xl font-bold text-white">{title}</h1>
            </div>
          </div>
          <p className="text-xl text-gray-200 max-w-2xl">{description}</p>
        </div>
      </section>

      {/* Long Description */}
      <section className="py-16 bg-gradient-to-b from-black to-gray-900">
        <div className="container mx-auto px-6">
          <div className="max-w-3xl mx-auto space-y-5">
            {longDescription.map((para, i) => (
              <p key={i} className="text-gray-300 text-lg leading-relaxed">{para}</p>
            ))}
          </div>
        </div>
      </section>

      {/* Features */}
      <section className="py-16 bg-gray-900">
        <div className="container mx-auto px-6">
          <h2 className="text-3xl font-bold text-center mb-12">
            {isEN ? 'What\'s Included' : 'Neler Dahil'}
            <span className="text-yellow-400"> {isEN ? '& How We Work' : '& Nasıl Çalışıyoruz'}</span>
          </h2>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {features.map((f, i) => (
              <div key={i} className="bg-black/50 border border-gray-800 rounded-xl p-6 hover:border-yellow-400/40 transition-colors">
                <div className="w-12 h-12 bg-yellow-400/10 border border-yellow-400/30 rounded-lg flex items-center justify-center mb-4">
                  <i className={`${f.icon} text-xl text-yellow-400`} />
                </div>
                <h3 className="text-lg font-semibold text-white mb-2">{f.title}</h3>
                <p className="text-gray-400 text-sm leading-relaxed">{f.text}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* FAQ */}
      {faq.length > 0 && (
        <section className="py-16 bg-black">
          <div className="container mx-auto px-6 max-w-3xl">
            <h2 className="text-3xl font-bold text-center mb-12">
              {isEN ? 'Frequently Asked' : 'Sık Sorulan'}
              <span className="text-yellow-400"> {isEN ? 'Questions' : 'Sorular'}</span>
            </h2>
            <div className="space-y-4">
              {faq.map((item, i) => (
                <details key={i} className="group bg-gray-900 border border-gray-800 rounded-xl overflow-hidden">
                  <summary className="flex items-center justify-between p-5 cursor-pointer list-none hover:bg-gray-800/50 transition-colors">
                    <span className="font-semibold text-white pr-4">{item.q}</span>
                    <i className="ri-add-line text-yellow-400 text-xl flex-shrink-0 group-open:hidden" />
                    <i className="ri-subtract-line text-yellow-400 text-xl flex-shrink-0 hidden group-open:block" />
                  </summary>
                  <div className="px-5 pb-5 text-gray-400 leading-relaxed">{item.a}</div>
                </details>
              ))}
            </div>
          </div>
        </section>
      )}

      {/* CTA */}
      <section className="py-16 bg-gradient-to-r from-yellow-400/10 to-yellow-500/5 border-y border-yellow-400/20">
        <div className="container mx-auto px-6 text-center">
          <h2 className="text-3xl font-bold text-white mb-4">
            {isEN ? 'Ready to Get Started?' : 'Başlamaya Hazır mısınız?'}
          </h2>
          <p className="text-gray-300 mb-8 max-w-xl mx-auto">
            {isEN
              ? 'Contact us today for a custom quote tailored to your event needs.'
              : 'Etkinlik ihtiyaçlarınıza özel teklif için bugün bize ulaşın.'}
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <Link
              to={`${homePath}#contact`}
              className="inline-flex items-center justify-center gap-2 px-8 py-4 bg-yellow-400 text-black font-bold rounded-lg hover:bg-yellow-300 transition-all duration-300 hover:scale-105 whitespace-nowrap"
            >
              <i className="ri-send-plane-line" />
              {isEN ? 'Request a Quote' : 'Teklif Talep Et'}
            </Link>
            <a
              href={whatsappHref()}
              target="_blank"
              rel="noopener noreferrer"
              className="inline-flex items-center justify-center gap-2 px-8 py-4 border border-gray-600 text-white font-semibold rounded-lg hover:border-yellow-400/50 hover:text-yellow-400 transition-all duration-300 whitespace-nowrap"
            >
              <i className="ri-whatsapp-line" />
              WhatsApp
            </a>
          </div>
        </div>
      </section>

      {/* Related Services */}
      {relatedServices.length > 0 && (
        <section className="py-16 bg-gray-900">
          <div className="container mx-auto px-6">
            <h2 className="text-2xl font-bold text-center mb-8 text-white">
              {isEN ? 'Related Services' : 'İlgili Hizmetler'}
            </h2>
            <div className="flex flex-wrap justify-center gap-4">
              {relatedServices.map((s) => (
                <Link
                  key={s.slug}
                  to={`${basePath}/${s.slug}`}
                  className="flex items-center gap-3 bg-black/50 border border-gray-800 rounded-xl px-5 py-3 hover:border-yellow-400/50 hover:text-yellow-400 transition-all duration-300 group"
                >
                  <i className={`${s.icon} text-lg text-yellow-400`} />
                  <span className="text-sm font-medium text-gray-300 group-hover:text-yellow-400 whitespace-nowrap">{s.title}</span>
                </Link>
              ))}
            </div>
          </div>
        </section>
      )}

      <Footer lang={lang} />
    </div>
  );
}
